import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import { CACHE_TOKEN, UNCACHE_TOKEN } from '../cache';
import type {
  AuthMethod,
  AuthResponse,
  AuthUser,
  GoogleSignInIntent,
  RegisterOptions,
  Tokens,
} from '@shared/models/auth';
import type { User, UserUpdate } from '@shared/models/user';
import { PROGRESS_TOKEN } from '../progress';

const progressContext = new HttpContext().set(PROGRESS_TOKEN, {
  section: 'staff',
});

@Injectable({ providedIn: 'root' })
export class AuthApi {
  readonly #baseUrl = environment.apiUrl;
  readonly #apiUrl = `${this.#baseUrl}/auth`;

  readonly #http = inject(HttpClient);

  getAuthMethods(email: string) {
    return this.#http.get<AuthMethod[]>(`${this.#apiUrl}/methods`, {
      params: { email },
    });
  }

  getAuthUser() {
    return this.#http.get<AuthUser>(`${this.#baseUrl}/users/me?roles=true`, {
      context: new HttpContext().set(CACHE_TOKEN, 'auth-user'),
    });
  }

  login(email: string, password: string) {
    return this.#http.post<AuthResponse>(`${this.#apiUrl}/login/password`, {
      email,
      password,
    });
  }

  getRefreshToken(refreshToken: string) {
    return this.#http.post<{ tokens: Tokens }>(
      `${this.#apiUrl}/refresh-token`,
      {
        refreshToken,
      },
    );
  }

  enablePassword(password: string, confirmPassword: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/settings/password/enable`,
      {
        password,
        confirmPassword,
      },
    );
  }

  changePassword(
    currentPassword: string,
    newPassword: string,
    confirmNewPassword: string,
  ) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/settings/password/change`,
      {
        currentPassword,
        newPassword,
        confirmNewPassword,
      },
    );
  }

  disablePassword(currentPassword: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/settings/password/disable`,
      { currentPassword },
    );
  }

  sendForgotPasswordOTP() {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/settings/password/forgot`,
      {},
    );
  }

  resetPasswordWithOTP(
    otp: string,
    newPassword: string,
    confirmNewPassword: string,
  ) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/settings/password/reset-otp`,
      { otp, newPassword, confirmNewPassword },
    );
  }

  requestPasswordReset(email: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/settings/password/reset`,
      {
        email,
      },
    );
  }

  resetPassword(
    token: string,
    email: string,
    password: string,
    verified = false,
  ) {
    return this.#http.post<{ success: boolean; accountVerified: boolean }>(
      `${this.#apiUrl}/settings/password/reset`,
      {
        token,
        email,
        password,
        verified,
      },
    );
  }

  logout() {
    return this.#http.post<{ success: boolean }>(`${this.#apiUrl}/logout`, {});
  }

  checkEmail(email: string) {
    return this.#http.post<{ emailExists: boolean }>(
      `${this.#apiUrl}/check-email`,
      {
        email,
      },
    );
  }

  /** Register staff and customer */
  register(opts: RegisterOptions) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/register/auth0`,
      {
        ...opts,
        locale: opts.locale || 'de',
      },
    );
  }

  registerOwner(opts: RegisterOptions) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/register/owner/auth0`,
      {
        ...opts,
        locale: opts.locale || 'de',
      },
    );
  }

  inviteStaff(opts: RegisterOptions) {
    return this.#http.post<{ success: boolean; user: User }>(
      `${this.#apiUrl}/register/staff`,
      {
        ...opts,
        locale: opts.locale || 'de',
      },
      { context: progressContext.set(UNCACHE_TOKEN, ['employees']) },
    );
  }

  inviteCustomer(opts: RegisterOptions) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/register/customer`,
      {
        ...opts,
        locale: opts.locale || 'de',
      },
    );
  }

  sendOTP(email: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/login/auth0/send-otp`,
      {
        email,
      },
    );
  }

  verifyOTP(email: string, otp: string) {
    return this.#http.post<AuthResponse>(
      `${this.#apiUrl}/login/auth0/verify-otp`,
      {
        email,
        otp,
      },
    );
  }

  /** Google sign-in via ID-token exchange (ADR 0007) — same response shape as the OTP/password endpoints */
  googleSignIn(idToken: string, intent: GoogleSignInIntent, context?: HttpContext) {
    return this.#http.post<AuthResponse>(
      `${this.#apiUrl}/login/google`,
      {
        idToken,
        intent,
      },
      { context },
    );
  }

  verifyRegistrationOTP(email: string, otp: string) {
    return this.#http.post<AuthResponse>(
      `${this.#apiUrl}/register/verify-otp`,
      {
        email,
        otp,
      },
    );
  }

  requestAccountDeletion(email: string, locale?: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/send-delete-account`,
      {
        email,
        locale,
      },
    );
  }

  confirmAccountDeletion(token: string, email: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/confirm-delete-account`,
      {
        token,
        email,
      },
    );
  }

  requestEmailChange(newEmail: string, locale?: string) {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/send-change-email`,
      {
        newEmail,
        locale,
      },
    );
  }

  confirmEmailChange(
    token: string,
    oldEmail: string,
    newEmail: string,
    password?: string,
  ) {
    const payload: any = {
      token,
      oldEmail,
      newEmail,
    };

    // Only include password if provided
    if (password !== undefined) {
      payload.password = password;
    }

    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/confirm-change-email`,
      payload,
    );
  }

  updateUser(userId: string, data: UserUpdate) {
    return this.#http.patch<{ success: boolean }>(
      `${this.#baseUrl}/users/${userId}`,
      data,
      {
        context: progressContext.set(UNCACHE_TOKEN, ['auth-user']),
      },
    );
  }

  sendOwnerWelcomeEmail() {
    return this.#http.post<{ success: boolean }>(
      `${this.#apiUrl}/send-owner-welcome-email`,
      {},
    );
  }
}
